import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { X } from "lucide-react";
import * as React from "react";

import { type Device } from "@/shared/domain/devices";
import { useDeleteDevice } from "@/shared/domain/devices/hooks/useDeleteDevice";
import { Button } from "@/shared/foundation/primitives";
import { RobotoDomainException } from "@/shared/http";

import { isDeleteConfirmed } from "./deleteDeviceConfirm";
import styles from "./deleteDeviceDialog.module.css";

interface DeleteDeviceDialogProps {
  // The device to delete; `null` keeps the dialog closed.
  device: Device | null;
  onClose: () => void;
  /** Called after the device is gone, to leave the now-dead detail route. */
  onDeleted: () => void;
}

export function DeleteDeviceDialog({
  device,
  onClose,
  onDeleted,
}: DeleteDeviceDialogProps) {
  const deleteDevice = useDeleteDevice();
  const [confirmText, setConfirmText] = React.useState("");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  // Clear the transient state each time the dialog opens on a device.
  React.useEffect(() => {
    if (device) {
      setConfirmText("");
      setSubmitError(null);
    }
  }, [device]);

  const confirmed = device
    ? isDeleteConfirmed(confirmText, device.device_id)
    : false;

  const onConfirm = async () => {
    if (!device || !confirmed) {
      return;
    }
    setSubmitError(null);
    try {
      await deleteDevice.mutateAsync({
        deviceId: device.device_id,
        orgId: device.org_id,
      });
      onDeleted();
    } catch (error) {
      // Stay open and surface the failure in-dialog so the user can retry.
      let errorMessage = "Failed to delete device. Please try again.";
      if (error instanceof RobotoDomainException) {
        errorMessage = error.message;
      }

      setSubmitError(errorMessage);
    }
  };

  return (
    <Dialog open={device !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={styles.title}>
        Delete device
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={styles.content}>
        {submitError ? <Alert severity="error">{submitError}</Alert> : null}

        <DialogContentText>
          Deleting <code>{device?.device_id}</code> permanently removes the
          device and its access tokens. This can&apos;t be undone.
        </DialogContentText>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Type <code>{device?.device_id}</code> to confirm
          </span>
          <TextField
            size="small"
            fullWidth
            autoComplete="off"
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && confirmed) {
                event.preventDefault();
                void onConfirm();
              }
            }}
          />
        </div>
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button variant="outlined" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void onConfirm()}
          loading={deleteDevice.isPending}
          disabled={!confirmed || deleteDevice.isPending}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
